import {IconName} from '@thinkeleven/fontawesome-svg/core';
import Icon from './icon';

interface CarouselData {
	interval: number;
	images: string[];
}

export default class Carousel {
	private static readonly carousels: Carousel[] = [];
	public static create(container: HTMLElement): Carousel {
		const carousel = new Carousel(container);
		this.carousels.push(carousel);
		return carousel;
	}
	public static async load(): Promise<void> {
		await Promise.all(this.carousels.map((carousel) => carousel.load()));
	}

	private readonly controls: Control[] = [new Control(this, 'prev', 'chevron-left'), new Control(this, 'next', 'chevron-right')];
	private readonly postId?: string;
	private readonly preloaded: Promise<void>;
	private interval!: number;
	private images?: string[];
	private slides!: Slide[];
	private timeout?: number;
	public inTransition: boolean = false;

	constructor(private readonly container: HTMLElement) {
		this.postId = this.container.dataset.carousel;
		this.preloaded = new Promise((resolve) => {
			this.preload().then(resolve);
		});
	}

	private async preload(): Promise<void> {
		if (!this.postId) return;
		const response = await fetch('/feeds/carousel?p=' + this.postId);
		const data: CarouselData = await response.json();
		if (!data) return;
		this.interval = data.interval;
		this.images = data.images;
		if (!this.images?.length) return;
		this.slides = this.images.map((image) => new Slide(this, image)).sort((a, b) => a.weight - b.weight);
		this.slides[0].load();
		for (const slide of this.slides) this.container.append(slide.element);
		this.cycle();
		this.container.append(...this.controls.map((control) => control.element));
	}

	public async load(): Promise<void> {
		await this.preloaded;
		for await (const slide of this.slides) await slide.load();
	}

	public async activateSlide(slideToActivate: Slide): Promise<void> {
		if (this.inTransition) return;
		this.inTransition = true;
		await slideToActivate.loaded;
		if (this.activeSlide) for (const slide of this.slides) slide.isPrevious = slide === this.activeSlide;
		setTimeout(() => {
			for (const slide of this.slides) slide.isActive = slide === slideToActivate;
		}, 0);
	}

	public async prev(): Promise<void> {
		let prev = this.activeSlide === undefined ? 0 : this.slides.indexOf(this.activeSlide) - 1;
		if (prev < 0) prev = this.slides.length - 1;
		await this.activateSlide(this.slides[prev]);
	}

	public async next(): Promise<void> {
		let next = this.activeSlide === undefined ? 0 : this.slides.indexOf(this.activeSlide) + 1;
		if (next >= this.slides.length) next = 0;
		await this.activateSlide(this.slides[next]);
	}

	public async cycle(): Promise<void> {
		await this.next();
		if (this.container.dataset.auto !== undefined) setTimeout(() => this.cycle(), this.interval);
	}

	public stop(): void {
		if (this.timeout === undefined) return;
		clearTimeout(this.timeout);
		this.timeout = undefined;
	}

	public get activeSlide(): Slide | undefined {
		return this.slides.find((slide) => slide.isActive);
	}
}

class Control {
	public readonly element: HTMLButtonElement = document.createElement('button');

	constructor(private readonly carousel: Carousel, public readonly action: 'prev' | 'next', icon: IconName) {
		this.element.className = 'carousel-control ' + this.action;
		this.element.type = 'button';
		this.element.append(Icon.create(icon));
		this.element.addEventListener('click', () => this.carousel[this.action]());
	}
}

class Slide {
	private isLoaded: boolean = false;
	private _isPrevious: boolean = false;
	private _isActive: boolean = false;
	public readonly weight: number = Math.random();
	public readonly element: HTMLDivElement = document.createElement('div');
	public readonly image: HTMLImageElement = new Image();
	public readonly loaded: Promise<void> = new Promise((resolve, reject) => {
		this.image.onload = () => resolve();
		this.image.onerror = () => reject();
	});

	constructor(private readonly carousel: Carousel, private readonly src: string) {
		this.element.className = 'slide';
		this.element.addEventListener('transitionend', () => {
			this.carousel.inTransition = false;
		});
	}

	public async load(): Promise<void> {
		if (this.isLoaded) return this.loaded;
		this.isLoaded = true;
		this.image.src = this.src;
		await this.loaded;
		this.element.style.backgroundImage = `url("${this.src}")`;
	}

	public set isPrevious(value: boolean) {
		this.element.classList.toggle('previous', value);
		this._isPrevious = value;
	}
	public get isPrevious(): boolean {
		return this._isPrevious;
	}

	public set isActive(value: boolean) {
		this.element.classList.toggle('active', value);
		this._isActive = value;
	}
	public get isActive(): boolean {
		return this._isActive;
	}
}
